import { inject, Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { NegotiationResponse } from '../interfaces/signalr.interface';
import { HttpHeaders } from '@angular/common/http';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class SignalrService implements OnDestroy {
  private api = inject(ApiService);
  private userService = inject(UserService);
  private hubConnection?: signalR.HubConnection;
  private isConnected = false;
  private isConnecting = false;
  private connectionStatus$ = new BehaviorSubject<boolean>(false);
  private subjects = new Map<string, BehaviorSubject<any>>();

  // Caching (unchanged)
  private cachedNegotiate: NegotiationResponse | null = null;
  private negotiateCachedAt = 0;
  private readonly CACHE_TTL_MS = 54 * 60 * 1000;
  private userId?: number;

  private keepAliveTimer?: any;
  private connectionTimedOut = new BehaviorSubject<boolean>(false);
  private timeoutTimer?: any;
  private readonly TIMEOUT_MS = 60000;

  
  constructor() {}

  isConnectionActive(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  connectionTimedOut$(): Observable<boolean> {
    return this.connectionTimedOut.asObservable();
  }

  private setConnected(value: boolean) {
    this.isConnected = value;
    this.connectionStatus$.next(value);
  }

  async startConnection(): Promise<void> {
    if (this.isConnected || this.isConnecting) {
      return;
    }
    
    this.isConnecting = true;
    if (!this.connectionTimedOut.value) {
      this.connectionTimedOut.next(false);
    }

    this.timeoutTimer = setTimeout(() => {
      if (!this.isConnected) {
        console.warn("⚠ SignalR not connected within 1 minute");
        this.connectionTimedOut.next(true);
      }
    }, this.TIMEOUT_MS);

    this.userId = Number(localStorage.getItem('ks_userid'));

    if (!this.userId || isNaN(this.userId) || this.userId <= 0) {
      this.isConnecting = false;
      setTimeout(() => this.startConnection(), 1000);
      return;
    }

    try {
      const negotiate = await this.getCachedOrFreshNegotiate(this.userId);
      this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(negotiate.Url, {
        accessTokenFactory: () => negotiate.AccessToken ?? '',
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect([0, 1000, 3000, 5000, 10000, 20000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
      this.setupEvents();
      await this.hubConnection.start();
      clearTimeout(this.timeoutTimer);
      this.connectionTimedOut.next(false);
      this.setConnected(true);
      this.sendPing();
      this.startKeepAlivePing();
      this.reRegisterAllHandlers();

    } catch (err: any) {
      console.error('SignalR handshake failed:', err?.message || err);
      if (err?.status === 404 || err?.message?.includes('timeout')) {
        console.warn('Azure connect timeout/404 → Retrying with jitter...');
        const jitter = Math.random() * 2000 + 1000;
        await new Promise(r => setTimeout(r, jitter));
        this.isConnecting = false;
        return this.startConnection();
      }

      this.setConnected(false);
      setTimeout(() => {
        this.isConnecting = false;
        this.startConnection();
      }, 5000);
    } 
    finally {
      if (!this.isConnected) {
        this.isConnecting = false;
      }
    }
  }

  private async getCachedOrFreshNegotiate(userId: number): Promise<NegotiationResponse> {
    const now = Date.now();

    if (this.cachedNegotiate && now - this.negotiateCachedAt < this.CACHE_TTL_MS) {
      return this.cachedNegotiate;
    }
    const response = await firstValueFrom(this.getTokenAndUrl(userId));

    if (response?.Url && response?.AccessToken) {
      this.cachedNegotiate = response;
      this.negotiateCachedAt = now;
    }
    return response;
  }

  private sendPing() {
    this.hubConnection?.send('__ping__').catch(() => {});
  }

  private startKeepAlivePing() {
    if (this.keepAliveTimer) clearInterval(this.keepAliveTimer);
    this.keepAliveTimer = setInterval(() => this.sendPing(), 15_000);
  }

  private setupEvents() {
    this.hubConnection!.onclose(() => {
      console.warn('SignalR disconnected');
      this.setConnected(false);
      this.isConnecting = false;
      this.stopKeepAlive();
      setTimeout(() => this.startConnection(), 5000);
    });

    this.hubConnection!.onreconnecting(() => {
      this.setConnected(false);
    });

    this.hubConnection!.onreconnected(() => {
      this.setConnected(true);
      this.sendPing();
      this.reRegisterAllHandlers();
    });
  }

  private reRegisterAllHandlers() {
    this.subjects.forEach((_, event) => this.registerHandler(event));
  }

  on<T>(eventName: string): Observable<T> {
    if (!this.subjects.has(eventName)) {
      this.subjects.set(eventName, new BehaviorSubject<T | null>(null));
    }
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      this.registerHandler(eventName);
    }
    return this.subjects.get(eventName)!.asObservable().pipe(
      filter((x): x is T => x !== null)
    ) as Observable<T>;
  }

  emit(method: string, ...args: any[]): Promise<void> {
    if (!this.isConnected) return Promise.reject('Not connected');
    return this.hubConnection!.invoke(method, ...args).catch(err =>
      console.error(`Invoke ${method} failed:`, err)
    );
  }

  private registerHandler(eventName: string) {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) return;
    this.hubConnection.off(eventName);
    this.hubConnection.on(eventName, (data: any) => {
      this.subjects.get(eventName)?.next(data);
    });
  }

  async stopConnection(): Promise<void> {
    this.stopKeepAlive();
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.setConnected(false);
      this.isConnecting = false;
    }
  }

  private stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stopKeepAlive();
    this.stopConnection();
    this.connectionStatus$.complete();
    this.subjects.clear();
  }

  getTokenAndUrl(userId: number): Observable<NegotiationResponse> {
    const headers = new HttpHeaders({
      'x-userid': userId.toString(),
    });
    return this.api.get<NegotiationResponse>('negotiate', undefined, headers);
  }
}